import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import BrandNav from '../../components/common/BrandNav';

// ── Color presets ─────────────────────────────────────────────────────────────
const COLOR_PRESETS = [
  // Light themes
  { bg: '#ffffff', ac: '#2196F3' }, { bg: '#ffffff', ac: '#E53935' },
  { bg: '#ffffff', ac: '#FF6F00' }, { bg: '#ffffff', ac: '#2E7D32' },
  { bg: '#ffffff', ac: '#6A1B9A' }, { bg: '#ffffff', ac: '#00838F' },
  { bg: '#FFF8E1', ac: '#E65100' }, { bg: '#E8F5E9', ac: '#1B5E20' },
  { bg: '#E3F2FD', ac: '#0D47A1' }, { bg: '#FCE4EC', ac: '#880E4F' },
  // Dark themes
  { bg: '#111827', ac: '#F59E0B' }, { bg: '#0F172A', ac: '#38BDF8' },
  { bg: '#1A1A2E', ac: '#E94560' }, { bg: '#064E3B', ac: '#34D399' },
  { bg: '#0a0f1e', ac: '#FFD700' }, { bg: '#1C1C1C', ac: '#E5E5E5' },
  { bg: '#18120a', ac: '#FF6B35' }, { bg: '#1a0a1e', ac: '#F472B6' },
];

// Helper: is a hex color light or dark?
function isLightColor(hex) {
  const h = (hex || '#fff').replace('#', '');
  if (h.length < 6) return true;
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 155;
}

// ── Phone Preview Component ───────────────────────────────────────────────────
function PhonePreview({ menuData, bgColor, accentColor, layout, gridCols, gridRows, lang, maxW, showPhotos }) {
  if (!menuData) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#bbb', fontSize: 14 }}>
      Loading menu preview…
    </div>
  );

  const { branch, categories, items } = menuData;
  const font = branch?.font || 'Cairo';
  const isAr = lang === 'ar';
  const dir  = isAr ? 'rtl' : 'ltr';
  const t = (en, ar) => (isAr && ar) ? ar : (en || '');
  const flatItems = Array.isArray(items) ? items : [];

  const light      = isLightColor(bgColor);
  const textColor  = light ? '#111' : '#fff';
  const subText    = light ? '#666' : 'rgba(255,255,255,0.65)';
  const cardBg     = light ? '#fff' : 'rgba(255,255,255,0.07)';
  const cardBorder = light ? '#E0E0E0' : 'rgba(255,255,255,0.12)';
  const stripBg    = light ? '#f0f0f0' : 'rgba(0,0,0,0.22)';

  // Design: 375px wide phone — scale to fit maxW
  const PHONE_W = 375;
  const PHONE_H = 580;
  const scale    = Math.min(1, maxW / PHONE_W);
  const displayW = Math.round(PHONE_W * scale);
  const displayH = Math.round(PHONE_H * scale);

  return (
    <div style={{
      width: displayW, height: displayH, overflow: 'hidden',
      borderRadius: Math.round(14 * scale), border: '2px solid #D0D0D0',
      boxShadow: '0 6px 28px rgba(0,0,0,0.13)', margin: '0 auto',
    }}>
      <div style={{
        width: PHONE_W, height: PHONE_H,
        transform: `scale(${scale})`, transformOrigin: 'top left',
        background: bgColor, fontFamily: `'${font}', sans-serif`,
        direction: dir, overflow: 'hidden',
      }}>

        {/* Header */}
        <div style={{ background: accentColor, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8, direction: 'ltr' }}>
          {branch?.logo_path ? (
            <img src={branch.logo_path} alt="logo"
              style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'contain', background: '#fff', padding: 2, flexShrink: 0 }} />
          ) : (
            <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16, color: '#fff', flexShrink: 0 }}>
              {(branch?.brand_name || '?').charAt(0).toUpperCase()}
            </div>
          )}
          <div style={{ flex: 1, fontWeight: 700, fontSize: 14, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {t(branch?.brand_name, branch?.brand_name_ar)}
          </div>
          <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 6, padding: '3px 8px', fontSize: 10, fontWeight: 700, color: '#fff', border: '1px solid rgba(255,255,255,0.3)', flexShrink: 0 }}>
            {isAr ? 'ع' : 'EN'}
          </div>
        </div>

        {/* Category strip */}
        <div style={{ background: stripBg, padding: '7px 10px', display: 'flex', gap: 6 }}>
          {categories.slice(0, 5).map((cat, i) => (
            <span key={cat.id} style={{
              padding: '4px 10px', borderRadius: 14, fontSize: 10, fontWeight: 600, flexShrink: 0,
              background: i === 0 ? accentColor : cardBg,
              color: i === 0 ? '#fff' : subText,
              border: `1px solid ${i === 0 ? 'transparent' : cardBorder}`,
            }}>
              {t(cat.name, cat.name_ar)}
            </span>
          ))}
        </div>

        {/* Items */}
        <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'hidden' }}>
          {categories.map(cat => {
            const catItems = flatItems.filter(i => i.category_id === cat.id);
            if (!catItems.length) return null;
            const shown = layout === 'grid'
              ? catItems.slice(0, gridCols * gridRows)
              : catItems.slice(0, 3);
            return (
              <div key={cat.id}>
                {/* Category divider */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                  <div style={{ flex: 1, height: 1, background: cardBorder }} />
                  <span style={{ fontSize: 9, fontWeight: 700, color: subText, textTransform: 'uppercase' }}>{t(cat.name, cat.name_ar)}</span>
                  <div style={{ flex: 1, height: 1, background: cardBorder }} />
                </div>

                {layout === 'list' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {shown.map(item => (
                      <div key={item.id} style={{ display: 'flex', background: cardBg, borderRadius: 8, border: `1px solid ${cardBorder}`, overflow: 'hidden' }}>
                        {showPhotos && (item.image_path ? (
                          <img src={item.image_thumb || item.image_path} alt=""
                            style={{ width: 54, height: 54, objectFit: 'contain', flexShrink: 0, background: light ? '#f5f5f5' : 'rgba(0,0,0,0.2)' }} />
                        ) : (
                          <div style={{ width: 54, height: 54, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, background: light ? '#f5f5f5' : 'rgba(0,0,0,0.2)' }}>
                            {item.icon && item.icon !== '🍽' ? item.icon : '🍽'}
                          </div>
                        ))}
                        <div style={{ padding: '7px 9px', flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: showPhotos ? 11 : 13, color: textColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>
                            {t(item.name, item.name_ar)}
                          </div>
                          <div style={{ fontWeight: 700, fontSize: showPhotos ? 11 : 12, color: accentColor }}>
                            {item.variants?.length > 0 ? item.variants.map(v => v.price).join(' / ') : item.price}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${gridCols}, 1fr)`, gap: 6 }}>
                    {shown.map(item => (
                      <div key={item.id} style={{ background: cardBg, borderRadius: 8, border: `1px solid ${cardBorder}`, overflow: 'hidden', textAlign: 'center' }}>
                        {showPhotos && (item.image_path ? (
                          <img src={item.image_thumb || item.image_path} alt=""
                            style={{ width: '100%', height: 54, objectFit: 'contain', background: light ? '#f5f5f5' : 'rgba(0,0,0,0.2)' }} />
                        ) : (
                          <div style={{ height: 54, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, background: light ? '#f5f5f5' : 'rgba(0,0,0,0.2)' }}>
                            {item.icon && item.icon !== '🍽' ? item.icon : '🍽'}
                          </div>
                        ))}
                        <div style={{ padding: 4 }}>
                          <div style={{ fontWeight: 600, fontSize: showPhotos ? 9 : 11, color: textColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {t(item.name, item.name_ar)}
                          </div>
                          <div style={{ fontWeight: 700, fontSize: showPhotos ? 9 : 11, color: accentColor }}>
                            {item.variants?.length > 0 ? item.variants[0].price : item.price}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Main QRPage ───────────────────────────────────────────────────────────────
export default function QRPage() {
  const { branchId, brandId } = useParams();
  // Remember last visited brand for sidebar quick-navigation
  useEffect(() => { if (brandId) localStorage.setItem("pm_last_brand", brandId); }, [brandId]);
  const navigate = useNavigate();

  const [qr, setQr]           = useState(null);
  const [menuData, setMenuData] = useState(null);
  const [loading, setLoading]  = useState(true);
  const [bgColor, setBgColor]  = useState('#ffffff');
  const [accentColor, setAccentColor] = useState('#2196F3');
  const [layout, setLayout]    = useState('list');
  const [showPhotos, setShowPhotos] = useState(true);
  const [branchList, setBranchList] = useState([]);   // for brand-wide ↔ branch QR switcher
  // Initial display language for the public menu when a customer scans the QR.
  // 'ar' (default) shows Arabic + RTL layout first; 'en' shows English + LTR.
  // The customer can still toggle inside the menu — this just sets the
  // first paint. Same value also drives the live preview to the right of
  // the Appearance row.
  const [initLang, setInitLang] = useState('ar');
  const [saving, setSaving] = useState(false);
  const [showPresets, setShowPresets] = useState(false);
  const [winW, setWinW]        = useState(() => window.innerWidth);

  useEffect(() => {
    const onResize = () => setWinW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    // Support both brand-level and branch-level QR
    const endpoint = brandId ? `/qr/brand/${brandId}` : `/qr/branch/${branchId}`;
    setLoading(true);
    api.get(endpoint)
      .then(async res => {
        setQr(res.data);
        try {
          // Extract token from short_url (e.g. https://…/menu/TOKEN)
          const url   = res.data.short_url || '';
          const token = url.match(/\/menu\/([^/?#]+)/)?.[1] || url.split('/').filter(Boolean).pop();
          if (token) {
            const mdRes = await api.get(`/menu/${token}`);
            setMenuData(mdRes.data);
            // Load saved theme settings from branch
            const branch = mdRes.data?.branch;
            if (branch?.theme_bg_color) setBgColor(branch.theme_bg_color);
            if (branch?.theme_accent_color) setAccentColor(branch.theme_accent_color);
            else if (branch?.brand_color) setAccentColor(branch.brand_color);
            if (branch?.theme_layout) setLayout(branch.theme_layout);
            if (branch?.theme_show_photos === false) setShowPhotos(false);
            if (branch?.theme_lang === 'en' || branch?.theme_lang === 'ar') setInitLang(branch.theme_lang);
          }
        } catch { /* menu data optional */ }
      })
      .catch(() => toast.error('Failed to load QR'))
      .finally(() => setLoading(false));
  }, [branchId, brandId]);

  // Fetch sibling branches so we can show a "switch to branch QR" dropdown.
  // Works in both modes:
  //   /brands/:brandId/qr     → fetch via brand_id
  //   /branches/:branchId/qr  → derive brand from menu data, then fetch
  useEffect(() => {
    let activeBrandId = brandId;
    if (!activeBrandId && menuData?.branch?.brand_id) activeBrandId = menuData.branch.brand_id;
    if (!activeBrandId) { setBranchList([]); return; }
    api.get(`/branches?brand_id=${activeBrandId}`)
      .then(r => setBranchList(r.data || []))
      .catch(() => setBranchList([]));
  }, [brandId, menuData]);

  const handleDownload = async () => {
    try {
      const endpoint = brandId ? `/qr/brand/${brandId}/png` : `/qr/branch/${branchId}/png`;
      const res = await api.get(endpoint, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a   = document.createElement('a');
      const filename = brandId ? `qr-brand-${brandId}.png` : `qr-branch-${branchId}.png`;
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const handleSaveTheme = async () => {
    setSaving(true);
    try {
      // Save theme to brand or branch depending on which QR page we're on
      const endpoint = brandId ? `/brands/${brandId}/theme` : `/branches/${branchId}/theme`;
      await api.put(endpoint, {
        theme_bg_color: bgColor,
        theme_accent_color: accentColor,
        theme_layout: layout,
        theme_show_photos: showPhotos,
        theme_lang: initLang,
      });
      toast.success('Theme saved!');
    } catch (err) {
      console.error('Theme save error:', err);
      toast.error('Failed to save theme');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="spinner" style={{ marginTop: 60 }} />;

  const isMobile    = winW <= 700;
  const previewMaxW = isMobile ? winW - 48 : 375; // full-size phone on desktop

  return (
    <div className="page" style={{ overflowX: 'hidden', boxSizing: 'border-box' }}>
      <style>{`
        /* Icon buttons with tooltips */
        .qr-icon-btn {
          width: 36px; height: 36px; border-radius: 8px;
          border: 1.5px solid #E0E0E0; background: #fff;
          cursor: pointer; display: flex; align-items: center;
          justify-content: center; font-size: 17px;
          transition: all 0.15s; position: relative; flex-shrink: 0;
        }
        .qr-icon-btn:hover { border-color: #1565C0; background: #E3F2FD; }
        .qr-icon-btn[data-tip]:hover::after {
          content: attr(data-tip);
          position: absolute; bottom: calc(100% + 6px); left: 50%;
          transform: translateX(-50%); background: #333; color: #fff;
          font-size: 11px; white-space: nowrap; padding: 3px 8px;
          border-radius: 5px; pointer-events: none; z-index: 200;
        }
        /* Mobile overrides — appearance row stays on a single line. Icons
           shrink rather than wrap, so the row never grows tall enough to
           push the preview card off-screen. */
        @media (max-width: 700px) {
          .qr-main-grid { grid-template-columns: 1fr !important; gap: 12px !important; }
          .page { padding: 10px !important; }
          .qr-prev-card { padding: 12px !important; }
          .qr-appearance { flex-wrap: nowrap !important; gap: 3px !important; padding: 0 !important; justify-content: space-between !important; }
          .qr-appearance .qr-icon-btn { width: 34px !important; height: 34px !important; font-size: 14px !important; }
          .qr-appearance .qr-lang-picker button { width: 28px !important; height: 34px !important; font-size: 11px !important; }
          .qr-appearance .qr-photos-toggle span { display: none !important; } /* hide "Photos" label, keep just the switch */
        }
      `}</style>

      {/* Header */}
      <div className="page-header">
        <div>
          {brandId
            ? <BrandNav brandId={brandId} subPath="qr"
                        backLabel="Menu Editor"
                        backPath={`/brands/${brandId}/menu`}
                        backKey="menu" />
            : <BrandNav brandId={null} subPath="qr" backLabel="Branches" backPath="/" backKey="branches" />
          }
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h1 className="page-title" style={{ margin: 0 }}>QR Code</h1>
            {/* Brand-wide ↔ branch QR switcher. Hidden if the brand has no
                branches yet (just the auto-Main one is fine — gives the user
                control to pick brand-level vs branch-level). */}
            {branchList.length > 0 && (
              <select
                value={branchId ? `branch:${branchId}` : (brandId ? `brand:${brandId}` : '')}
                onChange={e => {
                  const [kind, id] = e.target.value.split(':');
                  if (kind === 'brand')  navigate(`/brands/${id}/qr`);
                  if (kind === 'branch') navigate(`/branches/${id}/qr`);
                }}
                style={{
                  height: 36, padding: '0 12px', borderRadius: 10,
                  border: '1.5px solid #E0E0E0', fontSize: 13,
                  background: '#FAFAFA', cursor: 'pointer', minWidth: 180,
                  fontFamily: 'inherit',
                }}>
                {(brandId || menuData?.branch?.brand_id) && (
                  <option value={`brand:${brandId || menuData?.branch?.brand_id}`}>
                    🏷️ Brand-wide QR
                  </option>
                )}
                <optgroup label="Branches">
                  {branchList.map(b => (
                    <option key={b.id} value={`branch:${b.id}`}>
                      📍 {b.name}{b.name_ar ? ` · ${b.name_ar}` : ''}
                    </option>
                  ))}
                </optgroup>
              </select>
            )}
          </div>
        </div>
      </div>

      {/* ── Main Content - Vertical Layout ── */}
      <div style={{ maxWidth: 600, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* QR Card */}
        <div className="card" style={{ textAlign: 'center', padding: 24 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 20, color: '#333' }}>Scan to View Menu</h2>
          {qr && (
            <div style={{ display: 'inline-block', padding: 20, background: '#fff', border: '2px solid #E0E0E0', borderRadius: 16 }}>
              <QRCodeSVG value={qr.short_url} size={200} level="H" />
            </div>
          )}
          <p style={{ fontSize: 13, color: '#757575', margin: '16px 0' }}>
            One QR code per branch — customers scan to see your full menu
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            {/* Save Theme — sits with the other QR-card actions so the user
                can save the appearance edits in the same place they download
                / copy / open the QR. */}
            <button className="qr-icon-btn" data-tip="Save Appearance" onClick={handleSaveTheme}
              disabled={saving}
              style={{ width: 44, height: 44, fontSize: 20,
                       background: '#1565C0', color: '#fff', border: '2px solid #1565C0',
                       opacity: saving ? 0.6 : 1 }}>
              {saving ? '⏳' : '💾'}
            </button>
            <button className="qr-icon-btn" data-tip="Download PNG" onClick={handleDownload}
              style={{ width: 44, height: 44, fontSize: 20 }}>
              ⬇️
            </button>
            <button className="qr-icon-btn" data-tip="Copy Link"
              onClick={() => { navigator.clipboard.writeText(qr?.short_url); toast.success('Link copied!'); }}
              style={{ width: 44, height: 44, fontSize: 20 }}>
              📋
            </button>
            <button className="qr-icon-btn" data-tip="Open Menu"
              onClick={() => window.open(qr?.short_url, '_blank')}
              style={{ width: 44, height: 44, fontSize: 20 }}>
              🔗
            </button>
          </div>
        </div>

        {/* Appearance - Icon Row */}
        <div className="card" style={{ padding: '16px 24px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12, textAlign: 'center' }}>
            Appearance
          </div>
          {/* Single-row, never wrap. On narrow screens the row scrolls
              horizontally rather than breaking into two lines (which made
              the Save key fall off on its own row).
              The qr-appearance class hooks the mobile media query that
              shrinks each control to fit a phone screen without scrolling. */}
          <div className="qr-appearance"
               style={{ display: 'flex', gap: 8, justifyContent: 'center',
                        alignItems: 'center', flexWrap: 'nowrap',
                        overflowX: 'auto', WebkitOverflowScrolling: 'touch',
                        paddingBottom: 4 }}>
            {/* Preset */}
            <div style={{ position: 'relative' }}>
              <button className="qr-icon-btn" data-tip="Color Preset" onClick={() => setShowPresets(p => !p)}
                style={{
                  width: 44, height: 44, padding: 0,
                  background: `linear-gradient(135deg, ${bgColor} 50%, ${accentColor} 50%)`,
                  border: showPresets ? '2.5px solid #1565C0' : '1.5px solid #D0D0D0',
                }}>
              </button>
              {showPresets && (
                <>
                  <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setShowPresets(false)} />
                  <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 100, background: '#fff', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', padding: 14, width: `min(280px, calc(${winW}px - 24px))`, border: '1px solid #E0E0E0' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#999', textTransform: 'uppercase', marginBottom: 8 }}>Light</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                      {COLOR_PRESETS.filter(p => isLightColor(p.bg)).map((p, i) => (
                        <button key={i}
                          onClick={() => { setBgColor(p.bg); setAccentColor(p.ac); setShowPresets(false); }}
                          style={{ width: 28, height: 28, borderRadius: '50%', cursor: 'pointer', padding: 0, background: `linear-gradient(135deg,${p.bg} 50%,${p.ac} 50%)`, border: (bgColor === p.bg && accentColor === p.ac) ? '3px solid #1565C0' : '2px solid #ccc' }} />
                      ))}
                    </div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#999', textTransform: 'uppercase', marginBottom: 8 }}>Dark</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {COLOR_PRESETS.filter(p => !isLightColor(p.bg)).map((p, i) => (
                        <button key={i}
                          onClick={() => { setBgColor(p.bg); setAccentColor(p.ac); setShowPresets(false); }}
                          style={{ width: 28, height: 28, borderRadius: '50%', cursor: 'pointer', padding: 0, background: `linear-gradient(135deg,${p.bg} 50%,${p.ac} 50%)`, border: (bgColor === p.bg && accentColor === p.ac) ? '3px solid #1565C0' : '2px solid #ccc' }} />
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* BG color */}
            <label className="qr-icon-btn" data-tip="Background Color" style={{ width: 44, height: 44, padding: 4, cursor: 'pointer', position: 'relative', overflow: 'hidden' }}>
              <input type="color" value={bgColor} onChange={e => setBgColor(e.target.value)}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none', cursor: 'pointer' }} />
            </label>

            {/* Accent color */}
            <label className="qr-icon-btn" data-tip="Accent Color" style={{ width: 44, height: 44, padding: 4, cursor: 'pointer', position: 'relative', overflow: 'hidden' }}>
              <input type="color" value={accentColor} onChange={e => setAccentColor(e.target.value)}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none', cursor: 'pointer' }} />
            </label>

            <div style={{ width: 1, height: 32, background: '#E0E0E0', margin: '0 4px' }} />

            {/* List */}
            <button className="qr-icon-btn" data-tip="List View"
              onClick={() => setLayout('list')}
              style={{
                width: 44, height: 44, fontSize: 18,
                background: layout === 'list' ? '#1565C0' : 'transparent',
                color: layout === 'list' ? '#fff' : '#757575',
                border: layout === 'list' ? '2px solid #1565C0' : '1.5px solid #D0D0D0',
              }}>
              ☰
            </button>

            {/* Grid */}
            <button className="qr-icon-btn" data-tip="Grid View"
              onClick={() => setLayout('grid')}
              style={{
                width: 44, height: 44, fontSize: 18,
                background: layout === 'grid' ? '#1565C0' : 'transparent',
                color: layout === 'grid' ? '#fff' : '#757575',
                border: layout === 'grid' ? '2px solid #1565C0' : '1.5px solid #D0D0D0',
              }}>
              ⊞
            </button>

            <div style={{ width: 1, height: 32, background: '#E0E0E0', margin: '0 4px' }} />

            {/* Initial display language — sets which language the public menu
                opens in when a customer scans the QR. They can still toggle
                in-menu; this is just the first paint. Placed BEFORE the Photos
                toggle so language sits next to the layout controls (more
                conventional read order). */}
            <div className="qr-lang-picker" style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1.5px solid #D0D0D0', flexShrink: 0 }} data-tip="Default Language">
              <button onClick={() => setInitLang('ar')}
                title="Arabic (default)"
                style={{
                  width: 40, height: 44, padding: 0, border: 'none',
                  background: initLang === 'ar' ? '#1565C0' : '#fff',
                  color:      initLang === 'ar' ? '#fff'    : '#555',
                  fontWeight: 700, fontSize: 13, cursor: 'pointer',
                  fontFamily: 'Cairo, sans-serif',
                }}>AR</button>
              <div style={{ width: 1, background: '#D0D0D0' }} />
              <button onClick={() => setInitLang('en')}
                title="English"
                style={{
                  width: 40, height: 44, padding: 0, border: 'none',
                  background: initLang === 'en' ? '#1565C0' : '#fff',
                  color:      initLang === 'en' ? '#fff'    : '#555',
                  fontWeight: 700, fontSize: 13, cursor: 'pointer',
                }}>EN</button>
            </div>

            <div style={{ width: 1, height: 32, background: '#E0E0E0', margin: '0 4px' }} />

            {/* Show Photos toggle */}
            <label className="qr-photos-toggle" style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: '#555', fontWeight: 500, padding: '4px 2px', userSelect: 'none' }}>
              <div style={{
                width: 36, height: 20, borderRadius: 10, position: 'relative', flexShrink: 0,
                background: showPhotos ? '#2196F3' : '#ccc', transition: 'background 0.2s',
                cursor: 'pointer'
              }} onClick={() => setShowPhotos(p => !p)}>
                <div style={{
                  position: 'absolute', top: 2, left: showPhotos ? 18 : 2,
                  width: 16, height: 16, borderRadius: '50%', background: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s'
                }} />
              </div>
              <span style={{ whiteSpace: 'nowrap' }}>Photos</span>
            </label>

            {/* Save moved up to the QR-card actions row, so this Appearance
                row only carries the configuration controls (always one line). */}
          </div>
        </div>

        {/* ── Preview ── */}
        <div className="card qr-prev-card" style={{ padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Preview — how customers see your menu
          </div>
          <a href={qr?.short_url} target="_blank" rel="noreferrer"
            style={{ fontSize: 12, color: '#2196F3', textDecoration: 'none', fontWeight: 500 }}>
            Open full menu ↗
          </a>
        </div>

        <PhonePreview
          menuData={menuData}
          bgColor={bgColor}
          accentColor={accentColor}
          layout={layout}
          gridCols={2}
          gridRows={3}
          lang={initLang}
          maxW={previewMaxW}
          showPhotos={showPhotos}
        />
        </div>
      </div>
    </div>
  );
}
